import {
  Button,
  Divider,
  Group,
  NumberInput,
  Select,
  Stack,
  Textarea,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { client } from "@/api-client";
import type {
  GroupStatus,
  GroupTrainerOutput,
  GroupUpdateInput,
} from "@/api-types";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { getGroupStatusDisplayText } from "@/utils/groups";

interface EditGroupFormProps {
  group: GroupTrainerOutput;
  onClose: () => void;
  onSuccess?: () => void;
}

export const EditGroupForm = ({
  group,
  onClose,
  onSuccess,
}: EditGroupFormProps) => {
  const updateGroup = client.useMutation("put", "/trainer/groups/{groupId}");
  const deleteGroup = client.useMutation("delete", "/trainer/groups/{groupId}");
  const { navigate } = useNavigator();

  const form = useForm<GroupUpdateInput & { status: string }>({
    initialValues: {
      name: group.name,
      description: group.description || "",
      maxParticipants: group.maxParticipants,
      status: group.status,
    },
    validate: {
      name: (value) =>
        value && value.length < 2 ? "Name must be at least 2 characters" : null,
      maxParticipants: (value) =>
        value && value < 1 ? "Must have at least 1 participant" : null,
    },
  });

  const handleSubmit = async (
    values: GroupUpdateInput & {
      status: string;
    },
  ) => {
    const { status, ...updateData } = values;

    await updateGroup.mutateAsync({
      params: { path: { groupId: group.id } },
      body: {
        ...updateData,
        status: status as GroupStatus,
      },
    });

    onSuccess?.();
    onClose();
  };

  const handleDelete = async () => {
    if (
      !confirm(
        "Are you sure you want to delete this group? This action cannot be undone.",
      )
    ) {
      return;
    }

    await deleteGroup.mutateAsync({
      params: { path: { groupId: group.id } },
    });
    onSuccess?.();
    onClose();
    navigate({ path: UniversalRoutes.GROUPS.LIST });
  };

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <TextInput
          label="Group Name"
          placeholder="Enter group name"
          required
          {...form.getInputProps("name")}
        />

        <Textarea
          label="Description"
          placeholder="Describe what this group is about (optional)"
          rows={3}
          {...form.getInputProps("description")}
        />

        <NumberInput
          label="Maximum Participants"
          description="This is a guideline - you can approve more participants if needed"
          min={1}
          max={50}
          required
          {...form.getInputProps("maxParticipants")}
        />

        <Select
          label="Status"
          data={[
            { value: "OPEN", label: getGroupStatusDisplayText("OPEN") },
            {
              value: "OPEN_VIA_LINK",
              label: getGroupStatusDisplayText("OPEN_VIA_LINK"),
            },
            { value: "LOCKED", label: getGroupStatusDisplayText("LOCKED") },
            { value: "INACTIVE", label: getGroupStatusDisplayText("INACTIVE") },
          ]}
          {...form.getInputProps("status")}
        />

        <Group justify="flex-end">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={updateGroup.isPending || deleteGroup.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" loading={updateGroup.isPending}>
            Update Group
          </Button>
        </Group>

        <Divider />

        <Group justify="flex-end">
          <Button
            color="red"
            variant="outline"
            onClick={handleDelete}
            loading={deleteGroup.isPending}
          >
            Delete Group
          </Button>
        </Group>
      </Stack>
    </form>
  );
};
